import { RefObject, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ReactionArrow } from './ReactionArrow';
import { ChevronRightIcon } from '@alchemiesolns/ui';
import { convertNumbersToSubscripts } from '../utils/textFormatting';
import './ArrowSection.css';

export type ArrowType = 'single' | 'double';

const ARROW_THUMB_W = 36;
const ARROW_THUMB_H = 12;
const ARROW_THUMB_STROKE = 14;

const ARROW_TYPE_OPTIONS: {
  value: ArrowType;
  label: string;
}[] = [
  { value: 'single', label: 'Reaction arrow' },
  { value: 'double', label: 'Resonance arrow' },
];

interface ArrowSectionProps {
  topText: string;
  bottomText: string;
  onTopTextChange: (text: string) => void;
  onBottomTextChange: (text: string) => void;
  arrowType: ArrowType;
  onArrowTypeChange: (type: ArrowType) => void;
  reagentsRef?: RefObject<HTMLInputElement | null>;
  conditionsRef?: RefObject<HTMLInputElement | null>;
  onReagentsFocus?: () => void;
  onConditionsFocus?: () => void;
  onReagentsBlur?: () => void;
  onConditionsBlur?: () => void;
  /** Suffix for stable DOM ids when multiple ArrowSections mount (e.g. sidebar + modal). */
  fieldIdSuffix?: string;
}

export function ArrowSection({
  topText,
  bottomText,
  onTopTextChange,
  onBottomTextChange,
  arrowType,
  onArrowTypeChange,
  reagentsRef,
  conditionsRef,
  onReagentsFocus,
  onConditionsFocus,
  onReagentsBlur,
  onConditionsBlur,
  fieldIdSuffix = '',
}: ArrowSectionProps) {
  const reagentsInputId = `arrow-reagents-input${fieldIdSuffix}`;
  const typeTriggerId = `arrow-type-trigger${fieldIdSuffix}`;
  const conditionsInputId = `arrow-conditions-input${fieldIdSuffix}`;
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuRect, setMenuRect] = useState<{ top: number; left: number; width: number } | null>(null);
  const comboboxRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuPortalRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();
  const currentOption = ARROW_TYPE_OPTIONS.find((o) => o.value === arrowType) ?? ARROW_TYPE_OPTIONS[0];

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  const updateMenuPosition = useCallback(() => {
    const trig = triggerRef.current;
    if (!trig) return;
    const r = trig.getBoundingClientRect();
    setMenuRect({
      top: r.bottom + 4,
      left: r.left,
      width: r.width,
    });
  }, []);

  useLayoutEffect(() => {
    if (!menuOpen) {
      setMenuRect(null);
      return;
    }
    updateMenuPosition();
  }, [menuOpen, updateMenuPosition]);

  useEffect(() => {
    if (!menuOpen || typeof window === 'undefined') return;
    const sync = () => updateMenuPosition();
    window.addEventListener('resize', sync);
    window.addEventListener('scroll', sync, true);
    return () => {
      window.removeEventListener('resize', sync);
      window.removeEventListener('scroll', sync, true);
    };
  }, [menuOpen, updateMenuPosition]);

  useEffect(() => {
    if (!menuOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      const inTrigger = !!comboboxRef.current?.contains(target);
      const inMenu = !!menuPortalRef.current?.contains(target);
      if (!inTrigger && !inMenu) {
        setMenuOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [menuOpen]);

  return (
    <div className="arrow-section">
      <div className="arrow-controls-row">
        <div className="arrow-text-input-group">
          <div className="arrow-inset-field">
            <input
              id={reagentsInputId}
              ref={reagentsRef}
              type="text"
              value={topText}
              placeholder="Top arrow text"
              aria-label="Top arrow text"
              onChange={(e) => onTopTextChange(convertNumbersToSubscripts(e.target.value))}
              onFocus={onReagentsFocus}
              onBlur={onReagentsBlur}
              className="arrow-inset-field__input arrow-input-reagents"
            />
          </div>
        </div>

        <div className="arrow-control-group arrow-type-group">
          <div ref={comboboxRef} className={`arrow-type-combobox${menuOpen ? ' arrow-type-combobox--open' : ''}`}>
            <button
              ref={triggerRef}
              type="button"
              id={typeTriggerId}
              className="arrow-type-trigger"
              role="combobox"
              aria-label="Arrow type"
              aria-expanded={menuOpen}
              aria-controls={listboxId}
              aria-haspopup="listbox"
              onClick={() => setMenuOpen((o) => !o)}
            >
              <span className="arrow-type-trigger-thumb" aria-hidden="true">
                <ReactionArrow
                  arrowType={arrowType}
                  width={ARROW_THUMB_W}
                  height={ARROW_THUMB_H}
                  strokeWidth={ARROW_THUMB_STROKE}
                  className="arrow-svg arrow-type-trigger-svg"
                />
              </span>
              <span className="arrow-type-trigger-text">
                <span className="arrow-type-trigger-title">{currentOption.label}</span>
              </span>
              <ChevronRightIcon className="arrow-type-trigger-chevron" width={14} height={14} aria-hidden />
            </button>
            {typeof document !== 'undefined' &&
              menuOpen &&
              menuRect !== null &&
              createPortal(
                <div
                  ref={menuPortalRef}
                  id={listboxId}
                  className="arrow-type-menu arrow-type-menu--portal"
                  role="listbox"
                  aria-label="Arrow type"
                  style={{
                    top: menuRect.top,
                    left: menuRect.left,
                    width: menuRect.width,
                  }}
                >
                  {ARROW_TYPE_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      role="option"
                      aria-selected={opt.value === arrowType}
                      className={`arrow-type-option${opt.value === arrowType ? ' arrow-type-option--selected' : ''}`}
                      onClick={() => {
                        onArrowTypeChange(opt.value);
                        closeMenu();
                      }}
                    >
                      <span className="arrow-type-option-thumb" aria-hidden="true">
                        <ReactionArrow
                          arrowType={opt.value}
                          width={ARROW_THUMB_W}
                          height={ARROW_THUMB_H}
                          strokeWidth={ARROW_THUMB_STROKE}
                          className="arrow-svg arrow-type-option-svg"
                        />
                      </span>
                      <span className="arrow-type-option-text">
                        <span className="arrow-type-option-title">{opt.label}</span>
                      </span>
                    </button>
                  ))}
                </div>,
                document.body,
              )}
          </div>
        </div>

        <div className="arrow-text-input-group">
          <div className="arrow-inset-field">
            <input
              id={conditionsInputId}
              ref={conditionsRef}
              type="text"
              value={bottomText}
              placeholder="Bottom arrow text"
              aria-label="Bottom arrow text"
              onChange={(e) => onBottomTextChange(convertNumbersToSubscripts(e.target.value))}
              onFocus={onConditionsFocus}
              onBlur={onConditionsBlur}
              className="arrow-inset-field__input arrow-input-conditions"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
